import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { ArrowUpRight, ArrowLeft, Check, X, MapPin } from "lucide-react";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [others, setOthers] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/services/${slug}`).then((r) => { if (active) setService(r.data); }).catch(() => navigate("/services", { replace: true }));
    api.get("/services").then((r) => { if (active) setOthers(r.data.filter((s) => s.slug !== slug)); }).catch(() => {});
    return () => { active = false; };
  }, [slug, navigate]);

  if (!service) {
    return <div className="min-h-screen flex items-center justify-center text-gold font-serif text-xl">…</div>;
  }

  const gallery = service.gallery || [];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[480px] w-full overflow-hidden">
        {service.image ? (
          <img src={service.image} alt={service.title} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.18),transparent_60%),linear-gradient(to_bottom_right,#0d0d0d,#050505_60%,#1a1503)]">
            <div className="absolute right-0 bottom-0 font-serif text-[24rem] leading-none opacity-[0.04] select-none pr-8">
              {service.title.charAt(0)}
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16">
          <Reveal>
            <Link to="/services" data-testid="detail-back" className="inline-flex items-center gap-2 text-white/70 hover:text-gold text-sm uppercase tracking-widest mb-6">
              <ArrowLeft size={16} /> All Services
            </Link>
            <Overline>Service</Overline>
            <h1 className="mt-4 font-serif font-light text-5xl md:text-7xl leading-[0.95] max-w-4xl">{service.title}</h1>
            {service.short && <p className="mt-6 max-w-2xl text-white/70 font-body text-lg">{service.short}</p>}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a data-testid="detail-request-service" href={`mailto:info@giremodeling.net?subject=${encodeURIComponent("Service Request: " + service.title)}&body=${encodeURIComponent("Hi GI Remodeling,\n\nI'm interested in your " + service.title + " service. Please contact me with more information.\n\nName:\nPhone:\nAddress (Virginia):\nProject details:\n")}`} className="group inline-flex items-center justify-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300">
                Email Us <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
              </a>
              <a data-testid="detail-call" href="tel:+15712688498" className="inline-flex items-center justify-center gap-2 border border-white/25 text-white text-sm uppercase tracking-widest px-8 py-4 hover:border-gold hover:text-gold transition-colors">
                Call Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Overview + materials */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid lg:grid-cols-[1.4fr_1fr] gap-16">
        <Reveal>
          <Overline>Overview</Overline>
          {service.description ? (
            <p className="mt-6 font-serif font-light text-2xl md:text-3xl leading-snug text-white/90">{service.description}</p>
          ) : (
            <p className="mt-6 font-serif font-light text-2xl md:text-3xl leading-snug text-white/50 italic">
              Custom {service.title.toLowerCase()} designed to fit your home and budget. Contact us to discuss the details of your project.
            </p>
          )}
          <p className="mt-6 inline-flex items-center gap-2 text-white/50 text-sm uppercase tracking-widest"><MapPin size={15} className="text-gold" /> Available across Virginia · 1-Year Workmanship Warranty</p>
        </Reveal>
        {(service.materials || []).length > 0 && (
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-[#0a0a0a] p-8">
              <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-6">Materials & Custom Options</h3>
              <ul className="space-y-4">
                {(service.materials || []).map((m, i) => (
                  <li key={i} className="flex items-start gap-3 font-body text-white/80">
                    <Check size={18} className="text-gold mt-0.5 shrink-0" /> {m}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="border-y border-white/10 bg-[#080808] py-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal><Overline>Previous Work</Overline><h2 className="mt-6 font-serif font-light text-4xl md:text-5xl">Designs &amp; finishes</h2></Reveal>
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((g, i) => (
                <Reveal key={i} delay={(i % 3) * 0.07}>
                  <button data-testid={`detail-gallery-${i}`} onClick={() => setLightbox(g)} className="group block w-full overflow-hidden border border-white/10 aspect-[4/3]">
                    <img src={g} alt={`${service.title} ${i + 1}`} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl md:text-5xl">Ready for your {service.title.toLowerCase()} project?</h2>
          <Link data-testid="detail-cta" to={`/contact?service=${encodeURIComponent(service.title)}`} className="mt-8 inline-flex items-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300">
            Request a Free Quote <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>

      {/* Other services */}
      {others.length > 0 && (
        <section className="border-t border-white/10 py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <h3 className="text-xs uppercase tracking-[0.25em] text-white/50 mb-8">Explore other services</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {others.slice(0, 8).map((o) => (
                <Link key={o.id} to={`/services/${o.slug}`} data-testid={`other-service-${o.slug}`} className="group relative overflow-hidden aspect-[4/3] border border-white/10 hover:border-gold/40 transition-colors">
                  {o.image ? (
                    <img src={o.image} alt={o.title} className="h-full w-full object-cover opacity-40 group-hover:opacity-70 transition-opacity duration-500" />
                  ) : (
                    <div className="h-full w-full bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.12),transparent_60%),linear-gradient(to_bottom_right,#0d0d0d,#050505_60%,#1a1503)]">
                      <span className="absolute right-2 bottom-1 font-serif text-[6rem] leading-none opacity-[0.08] select-none">{o.title.charAt(0)}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  <span className="absolute bottom-3 left-3 right-3 font-serif text-lg leading-tight">{o.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
            data-testid="lightbox"
          >
            <button className="absolute top-6 right-6 text-white/70 hover:text-white" onClick={() => setLightbox(null)}><X size={28} /></button>
            <motion.img initial={{ scale: 0.95 }} animate={{ scale: 1 }} src={lightbox} alt="" className="max-h-[85vh] max-w-[90vw] object-contain border border-white/10" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
